import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    InputSignal,
    OnDestroy,
    signal,
    Signal,
    viewChild,
    WritableSignal,
} from '@angular/core';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
    IqEventBusTransformationService,
} from '@dat/iq-event-bus';
import { IqTooltipTooltipComponent } from '@dat/iq-tooltip';
import { isEmpty, isEqual } from 'lodash';
import z from 'zod';
import {
    addContributionsTooltip,
    addCurrentTooltip,
    addForecastTooltip,
    addHistoricalTooltip,
    forecastLegendMap,
    historicalLegendMap,
    LegendSettings,
    RateSeriesData,
    RateSeriesTypes,
    setUpCurrentGraph,
    setUpGraph,
    updateForecastGraph,
    updateHistoricalGraph,
} from './graph-builder';
import { DateTime } from 'luxon';
import { IqGraphRatesTimeTransformationService } from './iq-graph-rates-time-transformation.service';

const EventBusRateSchema = z.object({
    value: z.number(),
    date: z.string(),
    mileage: z.number(),
    fuelPerMile: z.number().optional().nullable(),
    fuelPerTrip: z.number().optional().nullable(),
});

const RateTimeRangeSchema = z.union([
    z.literal('historical.13MONTHS'),
    z.literal('forecast.13MONTHS'),
    z.literal('forecast.35DAYS'),
    z.literal('historical.current'),
]);
const RatePayloadSchema = z.object({
    id: z.string(),
    high: z.array(EventBusRateSchema),
    low: z.array(EventBusRateSchema),
    average: z.array(EventBusRateSchema),
    loaded: z.boolean(),
    errored: z.boolean(),
    timeEscalation: z.string().optional().nullable(),
    key: RateTimeRangeSchema,
});

const RateTypePayloadSchema = z.object({
    id: z.string(),
    rateType: z.union([
        z.literal('BrokerCarrierSpot'),
        z.literal('ShipperCarrierContract'),
    ]),
});
const TimeRangePayloadSchema = z.object({
    id: z.string(),
    timeRange: RateTimeRangeSchema,
});
const RateMapSchema = z.record(RateTimeRangeSchema, RatePayloadSchema);
const TripTypePayloadSchema = z.object({
    id: z.string(),
    perLoad: z.boolean(),
});
const CurrentRatePayloadSchema = z.object({
    id: z.string(),
    high: z.number(),
    low: z.number(),
    average: z.number(),
    timeEscalation: z.string(),
    loaded: z.boolean(),
    errored: z.boolean(),
});
const CurrentFuelSchema = z.object({
    id: z.string(),
    tripFuel: z.number(),
    mileFuel: z.number(),
});
const ContributionSchema = z.object({
    brokerName: z.string().or(z.number()).or(z.null()),
    brokerPaymentType: z.string(),
    carrierName: z.string().or(z.number()).or(z.null()),
    contributorType: z.string(),
    customerName: z.string().or(z.number()).or(z.null()),
    destinationCity: z.string(),
    destinationState: z.string(),
    destinationZipCode: z.number(),
    equipType: z.string(),
    id: z.number(),
    originCity: z.string(),
    originState: z.string(),
    originZipCode: z.number(),
    pickupDate: z.string(),
    shipperName: z.string().or(z.number()).or(z.null()),
    shipperPaymentType: z.string(),
    totalPaidByBroker: z.number(),
    totalPaidByShipper: z.number(),
});
const ContributionsPayloadSchema = z.object({
    id: z.string(),
    BrokerCarrierSpotContributions: z.array(ContributionSchema),
    ShipperCarrierContractContributions: z.array(ContributionSchema),
    errored: z.boolean(),
});

type ContributionsPayload = z.infer<typeof ContributionsPayloadSchema>;
type CurrentRatePayload = z.infer<typeof CurrentRatePayloadSchema>;
type EventBusRate = z.infer<typeof EventBusRateSchema>;
type TripTypePayload = z.infer<typeof TripTypePayloadSchema>;
type RateTypePayload = z.infer<typeof RateTypePayloadSchema>;
type RatePayload = z.infer<typeof RatePayloadSchema>;
type TimeRangePayload = z.infer<typeof TimeRangePayloadSchema>;
type RateTimeRange = z.infer<typeof RateTimeRangeSchema>;
type RateMap = z.infer<typeof RateMapSchema>;
type CurrentFuel = z.infer<typeof CurrentFuelSchema>;

//Austin Stubbing
enum Statuses {
    Loading = 'LOADING',
    None = 'NONE',
    Error = 'ERROR',
    Loaded = 'LOADED',
}
const StatusesSchema = z.nativeEnum(Statuses);

const StatusPayloadSchema = z.object({
    id: z.string(),
    status: StatusesSchema,
});
const RateInfoPayloadSchema = z.object({
    id: z.string(),
    bcsHigh: z.array(EventBusRateSchema),
    bcsLow: z.array(EventBusRateSchema),
    bcsAverage: z.array(EventBusRateSchema),
    sccHigh: z.array(EventBusRateSchema),
    sccLow: z.array(EventBusRateSchema),
    sccAverage: z.array(EventBusRateSchema),
    loaded: z.boolean(),
    errored: z.boolean(),
    timeEscalation: z.string().optional().nullable(),
    rateType: z.string().optional().nullable(),
    pageRateType: z.string().optional().nullable(),
    perLoad: z.boolean().optional().nullable(),
    key: RateTimeRangeSchema,
    currentAverage: z.array(EventBusRateSchema),
    currentHigh: z.array(EventBusRateSchema),
    currentLow: z.array(EventBusRateSchema),
    currentEscalation: z.string().optional().nullable(),
});
type RateInfoPayload = z.infer<typeof RateInfoPayloadSchema>;

@Component({
    selector: 'iq-graph-rates-time',
    standalone: true,
    imports: [CommonModule, IqTooltipTooltipComponent],
    templateUrl: './iq-graph-rates-time.component.html',
    styleUrl: './iq-graph-rates-time.component.scss',
})
export class IqGraphRatesTimeComponent implements OnDestroy, AfterViewInit {
    id: InputSignal<string> = input<string>('');

    private _eventBus: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );
    private _transformation: IqEventBusTransformationService = inject(
        IqEventBusTransformationService,
    );

    private _graphTransformations: IqGraphRatesTimeTransformationService =
        inject(IqGraphRatesTimeTransformationService);
    private _dispatchActions: Record<string, IqEventBusBrokerHandler> = {};
    private loadedEventDispatched: boolean = false;

    tooltipIcon: string = `<span class="text-caption mt-1 material-symbols-outlined">help</span>`;

    //#region Writable Signals
    rateTypePayload: WritableSignal<RateTypePayload> = signal({});
    tripTypePayload: WritableSignal<TripTypePayload> = signal({});
    currentTimeRange: WritableSignal<RateTimeRange> = signal(
        'historical.13MONTHS',
    );

    brokerCarrierSpotRateMap: WritableSignal<RateMap> = signal({
        'historical.13MONTHS': {
            id: '',
            high: [],
            low: [],
            average: [],
            loaded: false,
            errored: false,
            key: 'historical.13MONTHS',
        },
        'forecast.13MONTHS': {
            id: '',
            high: [],
            low: [],
            average: [],
            loaded: false,
            errored: false,
            key: 'forecast.13MONTHS',
        },
        'forecast.35DAYS': {
            id: '',
            high: [],
            low: [],
            average: [],
            loaded: false,
            errored: false,
            key: 'forecast.35DAYS',
        },
        'historical.current': {
            id: '',
            high: [],
            low: [],
            average: [],
            loaded: false,
            errored: false,
            key: 'historical.current',
        },
    } as RateMap);

    shipperCarrierContractRateMap: WritableSignal<RateMap> = signal({
        'historical.13MONTHS': {
            id: '',
            high: [],
            low: [],
            average: [],
            loaded: false,
            errored: false,
            key: 'historical.13MONTHS',
        },
        'forecast.13MONTHS': {
            id: '',
            high: [],
            low: [],
            average: [],
            loaded: false,
            errored: false,
            key: 'forecast.13MONTHS',
        },
        'forecast.35DAYS': {
            id: '',
            high: [],
            low: [],
            average: [],
            loaded: false,
            errored: false,
            key: 'forecast.35DAYS',
        },
        'historical.current': {
            id: '',
            high: [],
            low: [],
            average: [],
            loaded: false,
            errored: false,
            key: 'historical.current',
        },
    } as RateMap);

    /**
     * Determins which of the group of series ids are selected.
     *
     * This only applies to groups that aren't the current rate.
     */
    selectedRate: WritableSignal<RateSeriesTypes[]> = signal([]);
    //#endregion

    //#region Graph Setup
    graph: Signal<ElementRef> = viewChild<ElementRef>('graph');
    currentGraph: Signal<ElementRef> = viewChild<ElementRef>('currentGraph');
    chartInfo: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.DateAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
        contributionsXAxis: am5xy.DateAxis<any>;
    };
    currentChartInfo: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
    };

    isBrokerCarrierSpot: Signal<boolean> = computed(
        () => {
            return this.rateTypePayload().rateType === 'BrokerCarrierSpot';
        },
        { equal: isEqual },
    );

    isShipperCarrierContract: Signal<boolean> = computed(
        () => {
            return this.rateTypePayload().rateType === 'ShipperCarrierContract';
        },
        { equal: isEqual },
    );

    //#region Forecast Legend

    forecastLegend: Signal<LegendSettings[]> = computed(() => {
        const timeRange: string = this.currentTimeRange();

        if (!timeRange?.includes('forecast')) return [];

        const isBrokerCarrierSpot = this.isBrokerCarrierSpot();
        const isShipperCarrierContract = this.isShipperCarrierContract();
        const selectedRate = this.selectedRate();

        const shouldHideContract =
            isBrokerCarrierSpot && timeRange.includes('35DAYS');

        const legendNodes = this.generateForecastLegendNodes(
            isBrokerCarrierSpot,
            selectedRate,
            isShipperCarrierContract,
            shouldHideContract,
        );

        return legendNodes;
    });

    //#region Historical Legend
    historicalLegend: Signal<LegendSettings[]> = computed(
        () => {
            const timeRange: string = this.currentTimeRange();

            if (!timeRange?.includes('historical')) return [];

            const isBrokerCarrierSpot = this.isBrokerCarrierSpot();
            const isShipperCarrierContract = this.isShipperCarrierContract();
            const selectedRate = this.selectedRate();
            const contributions = isBrokerCarrierSpot
                ? this.contributionsPayload().BrokerCarrierSpotContributions
                : this.contributionsPayload()
                      .ShipperCarrierContractContributions;
            const legendNodes = this.generateHistoricalLegendNodes(
                isBrokerCarrierSpot,
                selectedRate,
                isShipperCarrierContract,
                !!contributions?.length,
            );

            return legendNodes;
        },
        { equal: isEqual },
    );
    //#endregion

    //#endregion

    //#region ComputedSignals
    loaded: Signal<boolean> = computed(() => {
        return true;
    });

    //#region Historical Data
    isHistorical: Signal<boolean> = computed(
        () => {
            return this.currentTimeRange()?.includes('historical');
        },
        { equal: isEqual },
    );
    brokerCarrierSpotRatesHistorical: Signal<RatePayload> = computed(
        (): RatePayload => {
            const timeRange: string = this.currentTimeRange();

            if (!timeRange.includes('historical')) return {};
            return this.brokerCarrierSpotRateMap()[timeRange] ?? {};
        },
    );
    brokerCarrierSpotRatesHistoricalCurrent: Signal<RatePayload> = computed(
        (): RatePayload => {
            const timeRange: string = this.currentTimeRange();

            if (!timeRange.includes('historical')) return {};

            return this.brokerCarrierSpotRateMap()['historical.current'] ?? {};
        },
        { equal: isEqual },
    );
    shipperCarrierContractRatesHistorical: Signal<RatePayload> = computed(
        (): RatePayload => {
            const timeRange: string = this.currentTimeRange();

            if (!timeRange.includes('historical')) return {};

            return this.shipperCarrierContractRateMap()[timeRange] ?? {};
        },
        { equal: isEqual },
    );
    shipperCarrierContractRatesHistoricalCurrent: Signal<RatePayload> =
        computed(
            (): RatePayload => {
                const timeRange: string = this.currentTimeRange();

                if (!timeRange.includes('historical')) return {};

                return (
                    this.shipperCarrierContractRateMap()[
                        'historical.current'
                    ] ?? {}
                );
            },
            { equal: isEqual },
        );
    brokerCarrierSpotRatesHistoricalCurrentSeriesData: Signal<RateSeriesData> =
        computed(
            () => {
                const payload = this.brokerCarrierSpotRatesHistoricalCurrent();
                const perLoad = this.tripTypePayload().perLoad;
                if (isEmpty(payload)) return {};
                return this.setUpPayload(payload, perLoad);
            },
            { equal: isEqual },
        );
    shipperCarrierContractRatesHistoricalCurrentSeriesData: Signal<RateSeriesData> =
        computed(
            () => {
                const payload =
                    this.shipperCarrierContractRatesHistoricalCurrent();
                const perLoad = this.tripTypePayload().perLoad;

                if (isEmpty(payload)) return {};

                return this.setUpPayload(payload, perLoad);
            },
            { equal: isEqual },
        );
    brokerCarrierSpotRatesHistoricalSeriesData: Signal<RateSeriesData> =
        computed((): RateSeriesData => {
            const payload = this.brokerCarrierSpotRatesHistorical();
            const perLoad = this.tripTypePayload().perLoad;
            if (isEmpty(payload)) return {};

            return this.setUpPayload(payload, perLoad);
        });
    shipperCarrierContractRatesHistoricalSeriesData: Signal<RateSeriesData> =
        computed(
            () => {
                const payload = this.shipperCarrierContractRatesHistorical();
                const perLoad = this.tripTypePayload().perLoad;

                if (isEmpty(payload)) return {};

                return this.setUpPayload(payload, perLoad);
            },
            { equal: isEqual },
        );
    //#endregion

    //#region Forecast Data
    isForecast: Signal<boolean> = computed(
        () => {
            return this.currentTimeRange()?.includes('forecast');
        },
        { equal: isEqual },
    );
    brokerCarrierSpotRatesForecast: Signal<RatePayload> = computed(
        () => {
            const timeRange: string = this.currentTimeRange();

            if (!timeRange.includes('forecast')) return {};

            return this.brokerCarrierSpotRateMap()[timeRange] ?? {};
        },
        { equal: isEqual },
    );
    shipperCarrierContractRatesForecast: Signal<RatePayload> = computed(
        () => {
            const timeRange: string = this.currentTimeRange();

            if (!timeRange.includes('forecast')) return {};

            return this.shipperCarrierContractRateMap()[timeRange] ?? {};
        },
        { equal: isEqual },
    );
    brokerCarrierSpotRatesForecastSeriesData: Signal<RateSeriesData> = computed(
        () => {
            const payload = this.brokerCarrierSpotRatesForecast();
            const perLoad = this.tripTypePayload().perLoad;
            if (isEmpty(payload)) return {};

            return this.setUpPayload(payload, perLoad);
        },
    );
    shipperCarrierContractRatesForecastSeriesData: Signal<RateSeriesData> =
        computed(
            () => {
                const payload = this.shipperCarrierContractRatesForecast();
                const perLoad = this.tripTypePayload().perLoad;

                if (isEmpty(payload)) return {};

                return this.setUpPayload(payload, perLoad);
            },
            { equal: isEqual },
        );
    //#endregion
    statusInfo: WritableSignal<{ loaded: boolean; errored: boolean }> = signal({
        loaded: false,
        errored: false,
    });
    contributionsPayload: WritableSignal<ContributionsPayload> = signal({});

    currentAverageRates = computed(
        () => {
            return (
                this.rateTypePayload().rateType === 'BrokerCarrierSpot'
                    ? this.brokerCarrierSpotRateMap()[this.currentTimeRange()]
                    : this.shipperCarrierContractRateMap()[
                          this.currentTimeRange()
                      ]
            ).average;
        },
        { equal: isEqual },
    );

    constructor() {
        this._graphTransformations.initialize();
        const handlerOpts = {
            parentId: this.id(),
        };

        this._dispatchActions = this._eventBus.registerMap({
            'type:graph-time-status-update': {
                callback: (payload: any) => {
                    if (this.id() !== payload.id) return;
                    const test = StatusPayloadSchema.safeParse(payload);
                    if (!test.success) {
                        console.warn(
                            'type:graph-time-status-update Invalid Payload',
                        );
                        return;
                    }

                    const currentInfo = this.statusInfo();
                    const statusUpdateMap: Record<Statuses, () => void> = {
                        [Statuses.Loading]: () => {
                            this.statusInfo.set({
                                ...currentInfo,
                                loaded: false,
                            });
                        },
                        [Statuses.None]: () => {
                            this.statusInfo.set({
                                ...currentInfo,
                                loaded: true,
                            });
                        },
                        [Statuses.Error]: () => {
                            this.statusInfo.set({
                                ...currentInfo,
                                errored: true,
                                loaded: true,
                            });
                        },
                        [Statuses.Loaded]: () => {
                            this.statusInfo.set({
                                ...currentInfo,
                                errored: false,
                                loaded: true,
                            });
                            if (!this.loadedEventDispatched) {
                                this._eventBus.dispatch({
                                    name: 'rateviewLoadedGraph',
                                    payload: {
                                        id: this.id(),
                                    }
                                });
                                this.loadedEventDispatched = true;
                            }
                        },
                    };
                    if (payload.status === 'LOADING') {
                        statusUpdateMap[payload.status]();
                    } else {
                        // Events cause the graph to flicker, so we add a delay to make it less noticeable while all data is still processing
                        setTimeout(() => {
                            statusUpdateMap[payload.status]();
                        });
                    }
                },
                opts: {
                    immediate: false
                }
            },
            'type:graph-rates-time-view-update': {
                callback: (payload: RateInfoPayload) => {
                    if (this.id() !== payload.id) return;
                    
                    this.rateTypePayload.set({
                        id: payload.id,
                        rateType: payload.rateType as
                            | 'BrokerCarrierSpot'
                            | 'ShipperCarrierContract',
                    });

                    const timeRangeMap = {
                        '13MHistorical': 'historical.13MONTHS',
                        '13MForecast': 'forecast.13MONTHS',
                        '35DForecast': 'forecast.35DAYS',
                    };
                    this.currentTimeRange.set(
                        timeRangeMap[payload.timeEscalation],
                    );

                    this.tripTypePayload.set({
                        id: payload.id,
                        perLoad: payload.perLoad,
                    });

                    this.brokerCarrierSpotRateMap.set({
                        ...this.brokerCarrierSpotRateMap(),
                        [payload.key]: {
                            id: payload.id,
                            high: payload.bcsHigh,
                            low: payload.bcsLow,
                            average: payload.bcsAverage,
                            loaded: payload.loaded,
                            errored: payload.errored,
                            key: payload.key,
                            timeEscalation: payload.timeEscalation,
                        },
                    });

                    this.shipperCarrierContractRateMap.set({
                        ...this.shipperCarrierContractRateMap(),
                        [payload.key]: {
                            id: payload.id,
                            high: payload.sccHigh,
                            low: payload.sccLow,
                            average: payload.sccAverage,
                            loaded: payload.loaded,
                            errored: payload.errored,
                            key: payload.key,
                            timeEscalation: payload.timeEscalation,
                        },
                    });

                    payload.rateType === 'BrokerCarrierSpot'
                        ? this.brokerCarrierSpotRateMap.set({
                              ...this.brokerCarrierSpotRateMap(),
                              ['historical.current']: {
                                  id: '',
                                  high: payload.currentHigh,
                                  low: payload.currentLow,
                                  average: payload.currentAverage,
                                  loaded: payload.loaded,
                                  errored: payload.errored,
                                  key: 'historical.current',
                                  timeEscalation: payload.currentEscalation,
                              },
                          })
                        : this.shipperCarrierContractRateMap.set({
                              ...this.shipperCarrierContractRateMap(),
                              ['historical.current']: {
                                  high: payload.currentHigh,
                                  low: payload.currentLow,
                                  average: payload.currentAverage,
                                  loaded: payload.loaded,
                                  errored: payload.errored,
                                  key: 'historical.current',
                                  timeEscalation: payload.currentEscalation,
                              },
                          });
                },
                ...handlerOpts,
            },
            setRateGraphContributions: {
                callback: (payload: ContributionsPayload) => {
                    if (this.id() !== payload.id) return;
                    this.contributionsPayload.set(payload);
                },
                ...handlerOpts,
            },
            'type:route:changed': {
                callback: () => {
                    this.loadedEventDispatched = false;
                },
                opts: {
                    immediate: false
                }
            }
        });

        //#region Forecast Graph
        effect(async () => {
            const timeRange: string = this.currentTimeRange();

            if (!timeRange?.includes('forecast')) return;
            const monthly = !timeRange.includes('DAYS');

            this.chartInfo.chart.hide(0);
            this.chartInfo.chart.series.clear();

            const brokerCarrierSpotForecast =
                this.brokerCarrierSpotRatesForecastSeriesData();
            const shipperCarrierContractForecast =
                this.shipperCarrierContractRatesForecastSeriesData();
            const perLoad = this.tripTypePayload().perLoad;

            const timeframe = timeRange.includes('DAYS') ? 'DAILY' : 'MONTHLY';

            updateForecastGraph(
                this.chartInfo,
                {
                    brokerCarrierSpotForecast,
                    shipperCarrierContractForecast,
                    currentRate: this.rateTypePayload().rateType,
                },
                {
                    perLoad,
                },
                { timeframe },
            );
            await this.toggleForecastSeriesVisibility();
            addForecastTooltip(
                this.chartInfo,
                this.forecastLegend(),
                this._transformation,
                {
                    brokerCarrierSpotForecast,
                    shipperCarrierContractForecast,
                    perLoad,
                    currentRate: this.rateTypePayload().rateType,
                },
                monthly,
            );
        });
        //#endregion

        //#region Historical Graph
        effect(async () => {
            const timeRange: string = this.currentTimeRange();
            const rateType = this.rateTypePayload().rateType;
            const timeframe = timeRange?.includes('DAYS') ? 'DAILY' : 'MONTHLY';

            if (!timeRange?.includes('historical')) return;

            this.chartInfo.chart.hide(0);
            this.currentChartInfo.chart.hide(0);
            this.chartInfo.chart.series.clear();
            this.currentChartInfo.chart.series.clear();

            const brokerCarrierSpotContributions =
                this.contributionsPayload().BrokerCarrierSpotContributions;
            const shipperCarrierContractContributions =
                this.contributionsPayload().ShipperCarrierContractContributions;

            const brokerCarrierSpotHistorical =
                this.brokerCarrierSpotRatesHistoricalSeriesData();
            const shipperCarrierContractHistorical =
                this.shipperCarrierContractRatesHistoricalSeriesData();
            const brokerCarrierSpotCurrent =
                this.brokerCarrierSpotRatesHistoricalCurrentSeriesData();
            const shipperCarrierContractCurrent =
                this.shipperCarrierContractRatesHistoricalCurrentSeriesData();
            const perLoad = this.tripTypePayload().perLoad;
            const showsContributions =
                this.selectedRate().includes('contributionsSpot') ||
                this.selectedRate().includes('contributionsContract');

            updateHistoricalGraph(
                this.chartInfo,
                {
                    brokerCarrierSpotHistorical,
                    shipperCarrierContractHistorical,
                    brokerCarrierSpotCurrent,
                    shipperCarrierContractCurrent,
                    perLoad,
                    currentRate: this.rateTypePayload().rateType,
                    currentGraphChartInfo: this.currentChartInfo,
                    brokerCarrierSpotContributions,
                    shipperCarrierContractContributions,
                },
                { timeframe },
                this._transformation,
                showsContributions,
                rateType
            );

            this.toggleHistoricalSeriesVisibility();

            addHistoricalTooltip(
                this.chartInfo,
                this.historicalLegend(),
                this._transformation,
                {
                    brokerCarrierSpotHistorical,
                    shipperCarrierContractHistorical,
                    brokerCarrierSpotCurrent,
                    shipperCarrierContractCurrent,
                    perLoad,
                    currentRate: this.rateTypePayload().rateType,
                    currentGraphChartInfo: this.currentChartInfo,
                    brokerCarrierSpotContributions,
                    shipperCarrierContractContributions,
                },
            );

            addCurrentTooltip(
                this.currentChartInfo,
                this.historicalLegend(),
                this._transformation,
                {
                    brokerCarrierSpotHistorical,
                    shipperCarrierContractHistorical,
                    brokerCarrierSpotCurrent,
                    shipperCarrierContractCurrent,
                    perLoad,
                    currentRate: this.rateTypePayload().rateType,
                    currentGraphChartInfo: this.currentChartInfo,
                    brokerCarrierSpotContributions,
                    shipperCarrierContractContributions,
                },
            );

            if (
                this.contributionsPayload().ShipperCarrierContractContributions
                    ?.length ||
                this.contributionsPayload().BrokerCarrierSpotContributions
                    ?.length
            ) {
                addContributionsTooltip(
                    this.chartInfo,
                    this.historicalLegend(),
                    this._transformation,
                    this.rateTypePayload().rateType,
                    perLoad,
                );
            }
        });
        effect(() => {
            this.toggleHistoricalSeriesVisibility();
        });

        effect(() => {
            this.toggleForecastSeriesVisibility();
        });
        //#endregion
    }

    // #region Lifecycle Hooks
    ngOnDestroy() {
        this._eventBus.unregisterMap(this._dispatchActions);
        this.chartInfo.root.dispose();
        this.currentChartInfo.root.dispose();
    }

    ngAfterViewInit(): void {
        this.chartInfo = setUpGraph(this.graph());
        this.currentChartInfo = setUpCurrentGraph(this.currentGraph());
    }
    //#endregion

    //#region Helper Functions

    /**
     * Toggles the visibility of the series based on the legend settings.
     *
     * This will be called when the legend is clicked and when the rate types
     * are modified.
     */
    private toggleHistoricalSeriesVisibility() {
        /**
         * Gets the current legend settings.
         */
        const historicalLegend = this.historicalLegend();

        /**
         * The current chart
         */
        const chart: am5xy.XYChart = this.chartInfo.chart;

        /**
         * Iterates over the series to check if the series id is in the legend settings.
         * And if so it will hide or show the series based on the checked property.
         */
        return chart.series.each((series) => {
            /**
             * The current series id.
             */
            const id = series.get('id');

            /**
             * The legend settings associated with the series id.
             */

            const legend = historicalLegend.find((l: LegendSettings): boolean =>
                l.seriesIds.includes(id),
            );

            if (!legend) return;

            /**
             * If the legend has a toggle property and it is not checked, then it will hide the series.
             */
            const shouldHide = legend?.toggle && !legend.checked;

            shouldHide ? series.hide(0) : series.show(0);
        });
    }
    /**
     * Toggles the visibility of the series based on the legend settings.
     *
     * This will be called when the legend is clicked and when the rate types
     * are modified.
     */
    private toggleForecastSeriesVisibility() {
        /**
         * Gets the current legend settings.
         */
        const forecastLegend = this.forecastLegend();

        /**
         * The current chart
         */
        const chart: am5xy.XYChart = this.chartInfo.chart;

        /**
         * Iterates over the series to check if the series id is in the legend settings.
         * And if so it will hide or show the series based on the checked property.
         */
        chart.series.each((series) => {
            /**
             * The current series id.
             */
            const id = series.get('id');
            /**
             * The legend settings associated with the series id.
             */
            const legend = forecastLegend.find((l: LegendSettings): boolean =>
                l.seriesIds.includes(id),
            );
            if (!legend) return;

            /**
             * If the legend has a toggle property and it is not checked, then it will hide the series.
             */
            const shouldHide = legend?.toggle && !legend.checked;
            shouldHide ? series.hide(0) : series.show(0);
        });
    }

    /**
     * Creates an array of legend node objects to be used inside of the
     * graph for the legend.
     *
     * @param isBrokerCarrierSpot - checks if the current rate is BrokerCarrierSpot
     * @param selectedRate - the array of selected rates
     * @param isShipperCarrierContract - checks if the current rate is ShipperCarrierContract
     * @returns an array of legend nodes based on the current state of the set up.
     */
    private generateHistoricalLegendNodes(
        isBrokerCarrierSpot: boolean,
        selectedRate: RateSeriesTypes[],
        isShipperCarrierContract: boolean,
        hasContributions: boolean,
    ) {
        /**
         * The order of the legend nodes based on the selected rate.
         */
        const order: RateSeriesTypes[] = isBrokerCarrierSpot
            ? ['brokerCarrierSpot', 'shipperCarrierContract']
            : ['shipperCarrierContract', 'brokerCarrierSpot'];

        /**
         * Add Contributions Legend at the end if Lane has contributions.
         */
        if (hasContributions) {
            order.push(
                isBrokerCarrierSpot
                    ? 'contributionsSpot'
                    : 'contributionsContract',
            );
        }
        /**
         * Checks to see if the brokerCarrierSpot is checked.
         *
         * It only applies if this rate is BrokerCarrierSpot.
         */
        const brokerCarrierSpotChecked = !isBrokerCarrierSpot
            ? selectedRate.includes('brokerCarrierSpot')
            : true;

        /**
         * Checks to see if the shipperCarrierContract is checked.
         *
         * It only applies if this rate is ShipperCarrierContract.
         */
        const shipperCarrierContractChecked = !isShipperCarrierContract
            ? selectedRate.includes('shipperCarrierContract')
            : true;

        const contributionsSpotChecked =
            selectedRate.includes('contributionsSpot');
        const contributionsContractChecked = selectedRate.includes(
            'contributionsContract',
        );

        /**
         * The updated settings for the legend nodes.
         */
        const updatedSettings: Partial<
            Record<RateSeriesTypes, Partial<LegendSettings>>
        > = {
            /**
             * The settings for the Broker Carrier Spot based on the current
             * state of the set up.
             */
            brokerCarrierSpot: {
                /**
                 * Only toggles if the current rate is Broker Carrier Spot
                 */
                toggle: !isBrokerCarrierSpot,
                /**
                 * Only applies if the current rate is Broker Carrier Spot
                 * and the brokerCarrierSpot group is selected
                 */
                checked: brokerCarrierSpotChecked,
                /**
                 * The color if checked.
                 */
                color: brokerCarrierSpotChecked
                    ? historicalLegendMap.brokerCarrierSpot.color
                    : 'grey',
            },
            shipperCarrierContract: {
                toggle: !isShipperCarrierContract,
                checked: shipperCarrierContractChecked,
                color: shipperCarrierContractChecked
                    ? historicalLegendMap.shipperCarrierContract.color
                    : 'grey',
            },
            contributionsSpot: {
                toggle: true,
                checked: contributionsSpotChecked,
                color: historicalLegendMap.contributionsSpot.color,
            },
            contributionsContract: {
                toggle: true,
                checked: contributionsContractChecked,
                color: historicalLegendMap.contributionsContract.color,
            },
        };

        /**
         * The legend nodes based on the order.
         *
         * This also iterates over the updated settings to apply the changes.
         */
        const legendNodes = order.map((type): LegendSettings => {
            return {
                ...historicalLegendMap[type],
                ...updatedSettings[type],
            };
        });

        return legendNodes;
    }

    /**
     * Creates an array of legend node objects to be used inside of the
     * graph for the legend.
     *
     * @param isBrokerCarrierSpot - checks if the current rate is BrokerCarrierSpot
     * @param selectedRate - the array of selected rates
     * @param isShipperCarrierContract - checks if the current rate is ShipperCarrierContract
     * @returns an array of legend nodes based on the current state of the set up.
     */
    private generateForecastLegendNodes(
        isBrokerCarrierSpot: boolean,
        selectedRate: RateSeriesTypes[],
        isShipperCarrierContract: boolean,
        shouldHideContract: boolean,
    ) {
        /**
         * The order of the legend nodes based on the selected rate.
         */
        const order: RateSeriesTypes[] = shouldHideContract
            ? ['brokerCarrierSpot']
            : isBrokerCarrierSpot
            ? ['brokerCarrierSpot', 'shipperCarrierContract']
            : ['shipperCarrierContract', 'brokerCarrierSpot'];
        /**
         * Checks to see if the brokerCarrierSpot is checked.
         *
         * It only applies if this rate is BrokerCarrierSpot.
         */
        const brokerCarrierSpotChecked = !isBrokerCarrierSpot
            ? selectedRate.includes('brokerCarrierSpot')
            : true;

        /**
         * Checks to see if the shipperCarrierContract is checked.
         *
         * It only applies if this rate is ShipperCarrierContract.
         */
        const shipperCarrierContractChecked = !isShipperCarrierContract
            ? selectedRate.includes('shipperCarrierContract')
            : true;

        /**
         * The updated settings for the legend nodes.
         */
        const updatedSettings: Partial<
            Record<RateSeriesTypes, Partial<LegendSettings>>
        > = {
            /**
             * The settings for the Broker Carrier Spot based on the current
             * state of the set up.
             */
            brokerCarrierSpot: {
                /**
                 * Only toggles if the current rate is Broker Carrier Spot
                 */
                toggle: !isBrokerCarrierSpot,
                /**
                 * Only applies if the current rate is Broker Carrier Spot
                 * and the brokerCarrierSpot group is selected
                 */
                checked: brokerCarrierSpotChecked,
                /**
                 * The color if checked.
                 */
                color: brokerCarrierSpotChecked
                    ? historicalLegendMap.brokerCarrierSpot.color
                    : 'grey',
            },
            shipperCarrierContract: {
                toggle: !isShipperCarrierContract,
                checked: shipperCarrierContractChecked,
                color: shipperCarrierContractChecked
                    ? historicalLegendMap.shipperCarrierContract.color
                    : 'grey',
            },
        };

        /**
         * The legend nodes based on the order.
         *
         * This also iterates over the updated settings to apply the changes.
         */
        const legendNodes = order.map((type): LegendSettings => {
            return {
                ...forecastLegendMap[type],
                ...updatedSettings[type],
            };
        });

        return legendNodes;
    }

    /**
     * Transforms the payload into data used with fuel, mileage, etc.
     *
     * @param payload - The payload to map with the information
     * @param perLoad - Determines if the payload is per load or not.
     * @returns a modified load
     */
    private setUpPayload(payload: RatePayload, perLoad: boolean) {
        const average = payload.average ?? [];
        const high = payload.high ?? [];
        const low = payload.low ?? [];

        const initialRates =
            average.reduce((acc, curr): RateSeriesData => {
                const highValue = high.find((h) => h.date === curr.date)?.value;

                const lowValue = low.find((h) => h.date === curr.date)?.value;

                const averageValue = curr.value;

                const timeEscalation =
                    {
                        SEVENDAYS: 'Last 7\n Days',
                        FIFTEENDAYS: 'Last 15\n Days',
                        THIRTYDAYS: 'Last 30\n Days',
                        SIXTYDAYS: 'Last 60\n Days',
                        NINETYDAYS: 'Last 90\n Days',
                        THREEDAYS: 'Last 3\n Days',
                        ONEYEAR: 'Last Year',
                        ONEHUNDREDEIGHTYDAYS: 'Last 180\n Days',
                        ONEMONTH: 'Last Month',
                    }[payload.timeEscalation ?? ''] ?? payload.timeEscalation;

                return {
                    ...acc,
                    [curr.date]: {
                        date: new Date(curr.date).getTime(),
                        key: curr.date,
                        high: highValue,
                        low: lowValue,
                        average: averageValue,
                        timeEscalation,
                    },
                };
            }, {} as RateSeriesData) ?? {};
        return initialRates;
    }

    //#endregion

    //#region Event Handlers
    toggleHistorical(item: LegendSettings) {
        if (!item.toggle) return;
        /**
         * Finds the rate group type associated with the clicked item.
         */
        const type: RateSeriesTypes = Object.keys(historicalLegendMap).find(
            (l) => historicalLegendMap[l].id === item.id,
        ) as RateSeriesTypes;

        /**
         * The current selected rate group.
         */
        const selectedRate: RateSeriesTypes[] = this.selectedRate();

        /**
         * If the item is checked, then it will remove the rate group from the selected rate.
         */
        const newRates: RateSeriesTypes[] = item.checked
            ? selectedRate.filter((r) => r !== type)
            : [...selectedRate, type];

        this.selectedRate.set(newRates);
    }

    toggleForecast(item: LegendSettings) {
        if (!item.toggle) return;

        /**
         * Finds the rate group type associated with the clicked item.
         */
        const type: RateSeriesTypes = Object.keys(forecastLegendMap).find(
            (l) => forecastLegendMap[l].id === item.id,
        ) as RateSeriesTypes;

        /**
         * The current selected rate group.
         */
        const selectedRate: RateSeriesTypes[] = this.selectedRate();
        /**
         * If the item is checked, then it will remove the rate group from the selected rate.
         */
        const newRates: RateSeriesTypes[] = item.checked
            ? selectedRate.filter((r) => r !== type)
            : [...selectedRate, type];
        this.selectedRate.set(newRates);
    }
    //#endregion
}
